<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import GrowthLinksAPI from 'dashboard/api/growthLinks';

const props = defineProps({
  linkId: { type: [String, Number], default: null },
});

const router = useRouter();
const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const accountId = getters.getCurrentAccountId.value;

const emptyUtm = () => ({ source: '', medium: '', campaign: '' });

const form = ref({
  name: '',
  inbox_id: null,
  greeting_text: '',
  funnel_id: null,
  stage_name: '',
  labels: [],
  ai_context: '',
  utm: emptyUtm(),
  active: true,
});
const labelsText = ref('');
const saved = ref(null); // resposta salva (com short_url etc.)
const qrSvg = ref('');
const isSaving = ref(false);

const inboxes = computed(
  () => getters['inboxes/getWhatsAppInboxes'].value || []
);
const funnels = computed(() => getters['funnels/getFunnels'].value || []);

// Desempenho do link (edição): cliques, leads e taxa de conversão.
const conversionRate = computed(() => {
  const clicks = saved.value?.click_count || 0;
  const leads = saved.value?.lead_count || 0;
  return clicks > 0 ? Math.round((leads / clicks) * 100) : 0;
});

// Etapas do funil selecionado (chave + label), ordenadas por position.
const funnelStages = computed(() => {
  const funnel = funnels.value.find(f => f.id === form.value.funnel_id);
  if (!funnel || !funnel.stages) return [];
  return Object.entries(funnel.stages)
    .map(([key, cfg]) => ({
      key,
      label: cfg?.label || key,
      position: cfg?.position ?? 999,
    }))
    .sort((a, b) => a.position - b.position);
});

// Ao trocar de funil, limpa a etapa se ela não pertencer ao novo funil.
watch(
  () => form.value.funnel_id,
  () => {
    if (!funnelStages.value.some(s => s.key === form.value.stage_name)) {
      form.value.stage_name = '';
    }
  }
);

const refreshQr = async () => {
  if (!saved.value?.id) return;
  const { data } = await GrowthLinksAPI.getQr(saved.value.id);
  qrSvg.value = data;
};

const load = async () => {
  store.dispatch('inboxes/get');
  store.dispatch('funnels/get');
  if (props.linkId) {
    const { data } = await GrowthLinksAPI.show(props.linkId);
    form.value = {
      name: data.name,
      inbox_id: data.inbox_id,
      greeting_text: data.greeting_text,
      funnel_id: data.funnel_id,
      stage_name: data.stage_name || '',
      labels: data.labels || [],
      ai_context: data.ai_context || '',
      utm: { ...emptyUtm(), ...(data.utm || {}) },
      active: data.active,
    };
    labelsText.value = (data.labels || []).join(', ');
    saved.value = data;
    await refreshQr();
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    form.value.labels = labelsText.value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    const { data } = props.linkId
      ? await GrowthLinksAPI.update(props.linkId, form.value)
      : await GrowthLinksAPI.create(form.value);
    saved.value = data;
    await refreshQr();
    useAlert(t('GROWTH_LINKS_MGMT.FORM.SAVE_SUCCESS'));
    if (!props.linkId) {
      router.replace({
        name: 'growth_links_edit',
        params: { accountId, linkId: data.id },
      });
    }
  } catch (error) {
    useAlert(error?.message || t('GROWTH_LINKS_MGMT.FORM.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const copyLink = () => {
  navigator.clipboard.writeText(saved.value.short_url);
  useAlert(t('GROWTH_LINKS_MGMT.RESULT.COPIED_TOAST'));
};

const downloadQr = () => {
  const blob = new Blob([qrSvg.value], { type: 'image/svg+xml' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `growth-link-${saved.value.code}.svg`;
  a.click();
  URL.revokeObjectURL(a.href);
};

onMounted(load);
</script>

<template>
  <div class="p-6 w-full">
    <h1 class="text-xl font-semibold text-n-slate-12 mb-6">
      {{
        props.linkId
          ? $t('GROWTH_LINKS_MGMT.FORM.TITLE_EDIT')
          : $t('GROWTH_LINKS_MGMT.FORM.TITLE_NEW')
      }}
    </h1>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
      <!-- Formulário (esquerda, 2 colunas) -->
      <div class="lg:col-span-2 space-y-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('GROWTH_LINKS_MGMT.FORM.NAME_LABEL') }}
          </label>
          <input
            v-model="form.name"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-sm"
            :placeholder="$t('GROWTH_LINKS_MGMT.FORM.NAME_PLACEHOLDER')"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('GROWTH_LINKS_MGMT.FORM.INBOX_LABEL') }}
          </label>
          <select
            v-model="form.inbox_id"
            class="w-full h-9 pl-3 pr-9 rounded-lg border border-n-weak bg-n-solid-1 text-sm"
          >
            <option :value="null" disabled>
              {{ $t('GROWTH_LINKS_MGMT.FORM.INBOX_PLACEHOLDER') }}
            </option>
            <option v-for="i in inboxes" :key="i.id" :value="i.id">
              {{ i.name }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('GROWTH_LINKS_MGMT.FORM.GREETING_LABEL') }}
          </label>
          <textarea
            v-model="form.greeting_text"
            rows="3"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-1 text-sm"
            :placeholder="$t('GROWTH_LINKS_MGMT.FORM.GREETING_PLACEHOLDER')"
          />
          <p class="text-xs text-n-slate-10 mt-1">
            {{ $t('GROWTH_LINKS_MGMT.FORM.GREETING_HINT') }}
          </p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ $t('GROWTH_LINKS_MGMT.FORM.FUNNEL_LABEL') }}
            </label>
            <select
              v-model="form.funnel_id"
              class="w-full h-9 pl-3 pr-9 rounded-lg border border-n-weak bg-n-solid-1 text-sm"
            >
              <option :value="null">
                {{ $t('GROWTH_LINKS_MGMT.FORM.FUNNEL_NONE') }}
              </option>
              <option v-for="f in funnels" :key="f.id" :value="f.id">
                {{ f.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ $t('GROWTH_LINKS_MGMT.FORM.STAGE_LABEL') }}
            </label>
            <select
              v-model="form.stage_name"
              :disabled="!form.funnel_id"
              class="w-full h-9 pl-3 pr-9 rounded-lg border border-n-weak bg-n-solid-1 text-sm disabled:opacity-50"
            >
              <option value="">
                {{ $t('GROWTH_LINKS_MGMT.FORM.STAGE_DEFAULT_OPTION') }}
              </option>
              <option v-for="s in funnelStages" :key="s.key" :value="s.key">
                {{ s.label }}
              </option>
            </select>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('GROWTH_LINKS_MGMT.FORM.LABELS_LABEL') }}
          </label>
          <input
            v-model="labelsText"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-sm"
            :placeholder="$t('GROWTH_LINKS_MGMT.FORM.LABELS_PLACEHOLDER')"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('GROWTH_LINKS_MGMT.FORM.AI_CONTEXT_LABEL') }}
          </label>
          <textarea
            v-model="form.ai_context"
            rows="2"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-1 text-sm"
            :placeholder="$t('GROWTH_LINKS_MGMT.FORM.AI_CONTEXT_PLACEHOLDER')"
          />
          <p class="text-xs text-n-slate-10 mt-1">
            {{ $t('GROWTH_LINKS_MGMT.FORM.AI_CONTEXT_HINT') }}
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ $t('GROWTH_LINKS_MGMT.FORM.UTM_LABEL') }}
          </label>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input
              v-model="form.utm.source"
              class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-sm"
              :placeholder="$t('GROWTH_LINKS_MGMT.FORM.UTM_SOURCE_PLACEHOLDER')"
            />
            <input
              v-model="form.utm.medium"
              class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-sm"
              :placeholder="$t('GROWTH_LINKS_MGMT.FORM.UTM_MEDIUM_PLACEHOLDER')"
            />
            <input
              v-model="form.utm.campaign"
              class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-1 text-sm"
              :placeholder="
                $t('GROWTH_LINKS_MGMT.FORM.UTM_CAMPAIGN_PLACEHOLDER')
              "
            />
          </div>
        </div>

        <label class="flex items-center gap-2 text-sm text-n-slate-11">
          <input v-model="form.active" type="checkbox" />
          {{ $t('GROWTH_LINKS_MGMT.FORM.ACTIVE_LABEL') }}
        </label>

        <button
          class="h-9 px-4 rounded-lg bg-n-brand text-white text-sm font-medium disabled:opacity-50"
          :disabled="
            isSaving || !form.name || !form.inbox_id || !form.greeting_text
          "
          @click="save"
        >
          {{
            isSaving
              ? $t('GROWTH_LINKS_MGMT.FORM.SAVING_BUTTON')
              : $t('GROWTH_LINKS_MGMT.FORM.SAVE_BUTTON')
          }}
        </button>
      </div>

      <!-- Resultado (direita, 1 coluna, fixo ao rolar) -->
      <aside class="lg:col-span-1 lg:sticky lg:top-6">
        <div
          v-if="saved"
          class="border border-n-weak rounded-xl p-5 space-y-4 bg-n-alpha-1"
        >
          <div v-if="props.linkId">
            <p class="text-xs uppercase text-n-slate-10 mb-2">
              {{ $t('GROWTH_LINKS_MGMT.RESULT.STATS_LABEL') }}
            </p>
            <div class="grid grid-cols-3 gap-2 text-center">
              <div class="rounded-lg bg-n-solid-1 py-2 px-1">
                <p class="text-lg font-bold text-n-slate-12 tabular-nums">
                  {{ saved.click_count }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{ $t('GROWTH_LINKS_MGMT.RESULT.STATS_CLICKS') }}
                </p>
              </div>
              <div class="rounded-lg bg-n-solid-1 py-2 px-1">
                <p class="text-lg font-bold text-n-slate-12 tabular-nums">
                  {{ saved.lead_count }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{ $t('GROWTH_LINKS_MGMT.RESULT.STATS_LEADS') }}
                </p>
              </div>
              <div class="rounded-lg bg-n-solid-1 py-2 px-1">
                <p class="text-lg font-bold text-n-slate-12 tabular-nums">
                  {{ `${conversionRate}%` }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{ $t('GROWTH_LINKS_MGMT.RESULT.STATS_CONVERSION') }}
                </p>
              </div>
            </div>
          </div>

          <div>
            <p class="text-xs uppercase text-n-slate-10 mb-1">
              {{ $t('GROWTH_LINKS_MGMT.RESULT.SHORT_LINK_LABEL') }}
            </p>
            <div class="flex items-center gap-2">
              <code class="text-sm text-n-slate-12 break-all">{{
                saved.short_url
              }}</code>
              <button class="text-xs text-n-brand shrink-0" @click="copyLink">
                {{ $t('GROWTH_LINKS_MGMT.RESULT.COPY_BUTTON') }}
              </button>
            </div>
          </div>
          <div>
            <p class="text-xs uppercase text-n-slate-10 mb-1">
              {{ $t('GROWTH_LINKS_MGMT.RESULT.PREVIEW_LABEL') }}
            </p>
            <pre class="text-sm text-n-slate-11 whitespace-pre-wrap">{{
              saved.prefilled_text
            }}</pre>
          </div>
          <div>
            <p class="text-xs uppercase text-n-slate-10 mb-2">
              {{ $t('GROWTH_LINKS_MGMT.RESULT.QR_LABEL') }}
            </p>
            <div
              class="bg-white p-3 rounded-xl w-fit shadow-sm [&>svg]:block [&>svg]:h-auto [&>svg]:max-w-full"
              v-html="qrSvg"
            />
            <button
              class="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-n-weak text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2"
              @click="downloadQr"
            >
              <span class="i-lucide-download size-3.5" />
              {{ $t('GROWTH_LINKS_MGMT.RESULT.DOWNLOAD_QR_BUTTON') }}
            </button>
          </div>
        </div>
        <div
          v-else
          class="border border-dashed border-n-weak rounded-xl p-8 text-center text-sm text-n-slate-10"
        >
          {{ $t('GROWTH_LINKS_MGMT.RESULT.EMPTY_HINT') }}
        </div>
      </aside>
    </div>
  </div>
</template>
